import React, { useState, useRef, useEffect } from "react";
import "./ChatWidget.css";
import logo from "../assets/icon.webp";

// ✅ Function to convert URLs into clickable HTML <a> links
function makeLinksClickable(text) {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  return text.replace(urlRegex, (url) => {
    return `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`;
  });
}

export default function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      sender: "bot",
      text: "👋 Hi! I’m your eSevaLink Assistant. Ask me anything about schemes, apply links, or help!"
    }
  ]);
  const [input, setInput] = useState("");

  const chatEndRef = useRef(null);

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const [showBubble, setShowBubble] = useState(true);
  const [bubbleClosed, setBubbleClosed] = useState(false);

  useEffect(() => {
    if (bubbleClosed) return; // If user manually closed, never reappear

    // Auto hide after 5 sec
    const hideTimer = setTimeout(() => {
      setShowBubble(false);

      // Reappear after 2 sec
      const showTimer = setTimeout(() => {
        setShowBubble(true);
      }, 2000);

      return () => clearTimeout(showTimer);
    }, 5000);

    return () => clearTimeout(hideTimer);
  }, [showBubble, bubbleClosed]);


  const sendMessage = async () => {
    if (!input.trim()) return;

    const userText = input;

    setMessages((prev) => [...prev, { sender: "user", text: userText }]);
    setInput("");

    // Add typing indicator
    setMessages((prev) => [...prev, { sender: "bot", typing: true }]);

    try {
      // Use environment variable for chatbot URL, fallback to production endpoint
      const CHATBOT_URL = process.env.REACT_APP_CHATBOT_URL || 
        (process.env.NODE_ENV === 'production' 
          ? 'https://esevalink.com/chat' 
          : 'http://localhost:8001/chat');
      
      const res = await fetch(CHATBOT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: userText })
      });

      const data = await res.json();

      // Remove typing bubble
      setMessages((prev) => prev.filter((msg) => !msg.typing));

      // Add bot response
      setMessages((prev) => [
        ...prev,
        { sender: "bot", text: data.response }
      ]);
    } catch (error) {
      // Remove typing bubble
      setMessages((prev) => prev.filter((msg) => !msg.typing));

      setMessages((prev) => [
        ...prev,
        { sender: "bot", text: "⚠️ Connection error. Please try again." }
      ]);
    }
  };

  return (
    <>
      {/* Floating Button */}

      {!isOpen && showBubble && !bubbleClosed && (
        <div className="chat-float-text">
          <button className="bubble-close-btn" onClick={() => setBubbleClosed(true)}>
            ×
          </button>

          👋 Hi, I'm eSeva Assistant,<br />
          How can I help?
        </div>
      )}


      {!isOpen && (
        <button className="chat-float-btn" onClick={() => setIsOpen(true)}>
          🤖
        </button>
      )}

      {isOpen && (
        <div className="chat-window">
          <header className="chat-header">
            <div className="chat-brand">
              <img src={logo} className="chat-logo" alt="EsevaLink" />
              <div>
                <h3>eSevaLink Assistant</h3>
                <span>AI Support</span>
              </div>
            </div>

            <button className="close-btn" onClick={() => setIsOpen(false)}>
              ✖
            </button>
          </header>

          <main className="chatbox">
            {messages.map((msg, i) => {
              // USER MESSAGE
              if (msg.sender === "user") {
                return (
                  <div key={i} className="user-message-row">
                    <div
                      className="user-message"
                      dangerouslySetInnerHTML={{ __html: msg.text }}
                    ></div>

                    <img
                      src="https://cdn-icons-png.flaticon.com/512/847/847969.png"
                      className="chat-avatar user-avatar"
                      alt="User"
                    />
                  </div>
                );
              }

              // BOT TYPING INDICATOR
              if (msg.typing) {
                return (
                  <div key={i} className="typing-row">
                    <img
                      src="https://cdn-icons-png.flaticon.com/512/4712/4712100.png"
                      className="chat-avatar"
                      alt="Bot"
                    />
                    <div className="typing-bubble">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                );
              }

              // BOT MESSAGE
              return (
                <div key={i} className="bot-message-row">
                  <img
                    src="https://cdn-icons-png.flaticon.com/512/4712/4712100.png"
                    className="chat-avatar"
                    alt="Bot"
                  />

                  <div
                    className="bot-message"
                    dangerouslySetInnerHTML={{
                      __html: makeLinksClickable(msg.text) // 🔥 Converts links automatically
                    }}
                  ></div>
                </div>
              );
            })}

            <div ref={chatEndRef}></div>
          </main>

          <footer className="chat-input-area">
            <input
              type="text"
              value={input}
              placeholder="Type your message..."
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && sendMessage()}
            />
            <button onClick={sendMessage}>➤</button>
          </footer>
        </div>
      )}
    </>
  );
}
